<template>
  <div class="share-flow-modal">
    <CustomModal @onClose="close">
      <div
        class="share-flow-modal-content-wrapper"
        :class="[customClass, { 'is-modal-loading': isModalLoading }]"
        ref="shareModal"
      >
        <div class="content" :class="{ 'is-modal-loading': isModalLoading }">
          <div v-if="isModalLoading" class="flow-link-loading-wrapper">
            <LoadingSpinner />
          </div>

          <template v-else>
            <div>
              <h3 class="content-title">Workflow teilen</h3>
            </div>
            <div
              class="share-flow-grid d-flex flex-column flex-md-row align-items-start"
              :class="{ 'single-column': flows.length }"
            >
              <div class="share-flow-main-column">
                <div class="flow-link-share-section" v-if="showFlowLinkSection">
                  <div class="flow-link-main-column">
                    <div class="flow-link-input-wrap">
                      <input :value="runWorkflowLink" readonly />
                      <button
                        class="copy-flow-link-btn"
                        type="button"
                        @click="copyRunWorkflowLink"
                      >
                        <i class="fa fa-link"></i>
                        <span>Link kopieren</span>
                      </button>
                    </div>
                    <span
                      class="copy-feedback"
                      :class="{ visible: isFlowLinkCopied }"
                      aria-live="polite"
                    >
                      Kopiert
                    </span>
                  </div>
                </div>

                <p class="content-paragraph">
                  Teilen Sie Ihren Flow mit anderen Personen.
                </p>

                <form @submit.prevent="save()">
                  <div class="input-field d-flex">
                    <input
                      class="email-input"
                      :class="{ 'has-error': emailValidationMessage }"
                      placeholder="E-Mail-Adresse"
                      v-model="shareParams.email"
                      type="text"
                      required
                      @blur="validateShareRecipients"
                      @input="clearEmailValidation"
                    />
                    <CustomDropdown className="permission-selector form-input">
                      <template v-slot:selected-option>
                        <span class="line-clamp"
                          >{{
                            getShareParamsAccessLevelName(
                              shareParams.accessLevel
                            )
                          }}
                        </span>
                      </template>
                      <template v-slot:option-data>
                        <b-dropdown-item
                          v-for="(level, index) in getDropDownAccessLevels"
                          :key="level.name"
                          @click="setShareParamsAccessLevel(level.name)"
                          :active="shareParams.accessLevel === level.name"
                        >
                          <template v-if="index !== 0">
                            {{ level.caption }}
                          </template>
                          <template v-if="index === 0">
                            <div class="d-flex justify-content-between w-100">
                              <span>{{ level.caption }}</span>

                              <span>
                                <img
                                  class="button-icon"
                                  :src="
                                    require('../../assets/img/chevron-down.svg')
                                  "
                              /></span>
                            </div>
                          </template>
                        </b-dropdown-item>
                      </template>
                    </CustomDropdown>
                  </div>
                  <div class="form-element-wrapper">
                    <p
                      v-if="emailValidationMessage"
                      class="email-validation-message"
                    >
                      {{ emailValidationMessage }}
                    </p>
                    <CommonButton
                      class="common-button-component"
                      :isLoading="isLoadingShareFlowButton"
                      :isDisabled="isLoadingShareFlowButton"
                    >
                      Teilen
                    </CommonButton>
                  </div>
                </form>
                <div class="flow-share-contact-info-wrap" v-if="!flows.length">
                  <template v-if="!isLoadingFlowPolicies">
                    <WorkflowSharePoliciesAccordion
                      heading="Benutzer mit Zugriff"
                      :policies="getAcceptedPolicies"
                      :loadingId="shareLoadingId"
                      :getDropDownAccessLevels="getDropDownAccessLevels"
                      :getShareParamsAccessLevelName="
                        getShareParamsAccessLevelName
                      "
                      :isCurrentUser="isCurrentUser"
                      @onDropdownOpen="onDropdownOpen"
                      @onDropdownClose="onDropdownClose"
                      @removePolicy="removePolicy"
                      @changePolicy="changePolicy"
                    />
                    <WorkflowSharePoliciesAccordion
                      v-if="getPendingPolicies.length"
                      heading="Offene Anfragen"
                      :policies="getPendingPolicies"
                      :loadingId="shareLoadingId"
                      :getDropDownAccessLevels="getDropDownAccessLevels"
                      :getShareParamsAccessLevelName="
                        getShareParamsAccessLevelName
                      "
                      :isCurrentUser="isCurrentUser"
                      @onDropdownOpen="onDropdownOpen"
                      @onDropdownClose="onDropdownClose"
                      @removePolicy="removePolicy"
                      @changePolicy="changePolicy"
                    />
                  </template>

                  <div v-if="isLoadingFlowPolicies" class="loading-wrapper">
                    <LoadingSpinner />
                  </div>
                </div>
              </div>
              <div class="flow-link-qr-wrap" v-if="showFlowLinkSection">
                <CommonQrGenerator
                  :key="runWorkflowLink"
                  :value="runWorkflowLink"
                  :size="188"
                  :card="false"
                  buttonStyle="outline"
                  :fileName="`${safeWorkflowName}-qr`"
                />
              </div>
            </div>
          </template>
        </div>
      </div>
    </CustomModal>
  </div>
</template>

<script>
import { workFlowAccessLevels } from '../../enum/auth.enum';
import {
  getWorkFlowAccessRights,
  shareWorkFlow,
  getWorkFlowById,
} from '../../services/workflow.service';

import CustomDropdown from '../widgets/CustomDropdown.vue';
import CustomModal from './CustomModal.vue';
import CommonButton from '../widgets/CommonButton.vue';
import LoadingSpinner from '../widgets/LoadingSpinner.vue';
import CommonQrGenerator from '../common/CommonQrGenerator.vue';
import {
  getIsVueBasedFormBuilder,
  getApiEndpointVersion,
} from '@/utils/helpers.js';

import { deleteWorkflowPolicy } from '@/services/axios/data.api';
import WorkflowSharePoliciesAccordion from '@/components/WorkFlow/WorkflowSharePoliciesAccordion.vue';
export default {
  name: 'ShareFlowModal',
  data() {
    return {
      accessLevels: workFlowAccessLevels,
      shareParams: {
        email: null,
        accessLevel: 'execute',
      },
      emailValidationMessage: '',
      flowPolicies: [],
      isLoadingFlowPolicies: false,
      isLoadingShareFlowButton: false,
      shareLoadingId: null,
      flowDetails: null,
      isFlowLinkCopied: false,
      copiedTimeout: null,
      isLoadingFlowLink: false,
      hasLoadedFlowDetails: false,
    };
  },
  props: {
    flowId: {
      type: Number,
      required: false,
    },
    flows: {
      type: Array,
      required: false,
      default: () => [],
    },
    customClass: {
      type: String,
      required: false,
      default: '',
    },
  },
  methods: {
    close: function () {
      this.$emit('close');
    },
    save: async function () {
      try {
        const recipients = this.getRecipients();
        if (!recipients.length) {
          return;
        }

        const { validRecipients, invalidRecipients } =
          this.partitionRecipients(recipients);

        if (!validRecipients.length) {
          this.emailValidationMessage = this.getInvalidEmailMessage(
            invalidRecipients.length ? invalidRecipients : recipients
          );
          return;
        }

        this.isLoadingShareFlowButton = true;
        const { succeededRecipients, failedRecipients, failedEntries } =
          await this.shareFlow({
            recipients: validRecipients,
            access_level: this.shareParams.accessLevel,
          });

        const remainingRecipients = this.getUniqueRecipients([
          ...invalidRecipients,
          ...failedRecipients,
        ]);

        if (remainingRecipients.length > 0) {
          this.$set(this.shareParams, 'email', remainingRecipients.join(', '));
          this.emailValidationMessage = this.getShareIssueMessage(
            invalidRecipients,
            failedEntries
          );
        } else {
          this.$set(this.shareParams, 'email', null);
          this.emailValidationMessage = '';
        }

        if (succeededRecipients.length > 0) {
          this.showToaster(
            'Workflow wurde erfolgreich geteilt',
            'Erfolg',
            'success'
          );
          this.$emit('save');
        }

        if (failedRecipients.length > 0) {
          this.showToaster(
            this.getFailedShareMessage(failedEntries),
            'Fehlerhaft !',
            'danger'
          );
        }
      } catch (error) {
        this.showToaster(error.message, 'Fehlerhaft !', 'danger');
      } finally {
        this.isLoadingShareFlowButton = false;
      }
    },
    validateShareRecipients() {
      const recipients = this.getRecipients();
      const invalidRecipients = this.getInvalidRecipients(recipients);

      this.emailValidationMessage = invalidRecipients.length
        ? this.getInvalidEmailMessage(invalidRecipients)
        : '';
    },
    clearEmailValidation() {
      if (this.emailValidationMessage) {
        this.emailValidationMessage = '';
      }
    },
    getRecipients(source = this.shareParams.email) {
      return String(source || '')
        .split(',')
        .map((email) => email.trim().toLowerCase())
        .filter(Boolean);
    },
    getUniqueRecipients(recipients) {
      return [...new Set(recipients.filter(Boolean))];
    },
    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },
    getInvalidRecipients(recipients) {
      return recipients.filter((recipient) => !this.isValidEmail(recipient));
    },
    partitionRecipients(recipients) {
      return recipients.reduce(
        (result, recipient) => {
          if (this.isValidEmail(recipient)) {
            result.validRecipients.push(recipient);
          } else {
            result.invalidRecipients.push(recipient);
          }
          return result;
        },
        { validRecipients: [], invalidRecipients: [] }
      );
    },
    getInvalidEmailMessage(invalidRecipients) {
      if (!invalidRecipients.length) {
        return '';
      }

      return `Ungültige E-Mail: ${invalidRecipients.join(', ')}`;
    },
    getFailedShareMessage(failedEntries) {
      if (!failedEntries.length) {
        return '';
      }

      const recipientList = failedEntries
        .map((entry) => entry.recipient)
        .join(', ');
      const message = failedEntries[0].reason.data.message;

      if (message.includes('The Access of the owner cannot be changed')) {
        return `Dieser User kann nicht eingeladen werden: ${recipientList}`;
      }

      if (message.includes('E-mail not found')) {
        return `E-Mail nicht gefunden: ${recipientList}`;
      }

      return `Diese E-Mail konnte nicht geteilt werden: ${recipientList}`;
    },
    getShareIssueMessage(invalidRecipients, failedRecipients) {
      if (invalidRecipients.length) {
        return this.getInvalidEmailMessage(invalidRecipients);
      }

      if (failedRecipients.length) {
        return this.getFailedShareMessage(failedRecipients);
      }

      return '';
    },
    async shareFlow({ recipient, recipients, access_level }) {
      const targetRecipients = recipients?.length
        ? recipients
        : this.getRecipients(recipient);

      if (!targetRecipients.length) {
        return {
          succeededRecipients: [],
          failedRecipients: [],
          failedEntries: [],
        };
      }

      const results = await Promise.allSettled(
        targetRecipients.map((targetRecipient) => {
          const shareRequests = !this.flows.length
            ? [
                shareWorkFlow(
                  {
                    recipient: targetRecipient,
                    flow_id: this.flowId,
                    access_level,
                  },
                  true
                ),
              ]
            : this.flows.map((flow) =>
                shareWorkFlow(
                  {
                    recipient: targetRecipient,
                    flow_id: flow.id,
                    access_level,
                  },
                  true
                )
              );

          return Promise.all(shareRequests);
        })
      );

      const succeededRecipients = [];
      const failedRecipients = [];
      const failedEntries = [];

      results.forEach((result, index) => {
        const targetRecipient = targetRecipients[index];

        if (result.status === 'fulfilled') {
          succeededRecipients.push(targetRecipient);
        } else {
          failedRecipients.push(targetRecipient);
          failedEntries.push({
            recipient: targetRecipient,
            reason: result.reason,
          });
        }
      });

      if (!this.flows.length && succeededRecipients.length > 0) {
        await this.getAccessRights();
      }

      return {
        succeededRecipients,
        failedRecipients,
        failedEntries,
      };
    },
    async getAccessRights() {
      try {
        const response = await getWorkFlowAccessRights(this.flowId);

        if (
          response &&
          response.status === 200 &&
          response.data &&
          response.data.length > 0
        ) {
          this.flowPolicies = response.data;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log({ error });
      }
    },
    async loadFlowDetails() {
      try {
        if (this.flows.length || !this.flowId) {
          this.hasLoadedFlowDetails = true;
          return;
        }
        this.isLoadingFlowLink = true;
        this.hasLoadedFlowDetails = false;
        const response = await getWorkFlowById(this.flowId);
        if (response && response.status === 200 && response.data) {
          this.flowDetails = response.data;
        }
      } catch (error) {
        this.flowDetails = null;
      } finally {
        this.isLoadingFlowLink = false;
        this.hasLoadedFlowDetails = true;
      }
    },
    async copyRunWorkflowLink() {
      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(this.runWorkflowLink);
        } else {
          const dummy = document.createElement('textarea');
          dummy.value = this.runWorkflowLink;
          document.body.appendChild(dummy);
          dummy.select();
          document.execCommand('copy');
          document.body.removeChild(dummy);
        }

        this.isFlowLinkCopied = true;
        if (this.copiedTimeout) clearTimeout(this.copiedTimeout);
        this.copiedTimeout = setTimeout(() => {
          this.isFlowLinkCopied = false;
        }, 1800);
      } catch (error) {
        this.showToaster(
          'Link konnte nicht kopiert werden',
          'Fehler',
          'danger'
        );
      }
    },
    async removePolicy(p) {
      try {
        this.shareLoadingId = p.id;
        const payload = {
          policy_id: p.id,
          flow_id: p.resource,
        };
        const response = await deleteWorkflowPolicy(payload);
        if (response && response.status === 200) {
          await this.getAccessRights();
          this.showToaster(
            'Die Berechtigung wurde erfolgreich entfernt',
            'Erfolg',
            'success'
          );
        }
      } catch (error) {
        this.showToaster(
          'Die Berechtigung konnte nicht entfernt werden',
          'Fehlerhaft !',
          'danger'
        );
      } finally {
        this.shareLoadingId = null;
      }
    },
    changePolicy: async function (p, accessName) {
      try {
        this.shareLoadingId = p.id;
        const { succeededRecipients, failedRecipients, failedEntries } =
          await this.shareFlow({
            recipient: p.email,
            access_level: accessName,
          });

        if (succeededRecipients.length > 0) {
          this.showToaster(
            'Die Berechtigung wurde erfolgreich angepasst',
            'Erfolg',
            'success'
          );
        }

        if (failedRecipients.length > 0) {
          this.showToaster(
            this.getFailedShareMessage(failedEntries),
            'Fehlerhaft !',
            'danger'
          );
        }
      } catch (error) {
        this.showToaster(error.message, 'Fehlerhaft !', 'danger');
      } finally {
        this.shareLoadingId = null;
      }
    },
    setShareParamsAccessLevel(accessName) {
      this.shareParams.accessLevel = accessName;
    },
    getShareParamsAccessLevelName(accessLevel) {
      return this.accessLevels.find((level) => level.name == accessLevel)
        .caption;
    },
    isCurrentUser(userEmail) {
      return this.user.attributes.email === userEmail;
    },
    async loadFlowPolicies() {
      try {
        if (!this.flows.length) {
          this.isLoadingFlowPolicies = true;
          await this.getAccessRights();
        }
      } catch (error) {
        //
      } finally {
        this.isLoadingFlowPolicies = false;
      }
    },

    onDropdownOpen() {
      this.isUserIdentityDropdownOpen = true;
    },
    onDropdownClose() {
      this.isUserIdentityDropdownOpen = false;
    },
    showToaster(message, title, variant) {
      this.$bvToast.toast(message, {
        title,
        variant,
      });
    },
  },
  created() {
    this.loadFlowPolicies();
    this.loadFlowDetails();
  },
  beforeDestroy() {
    if (this.copiedTimeout) clearTimeout(this.copiedTimeout);
  },
  computed: {
    getDropDownAccessLevels() {
      return this.accessLevels.filter((el) => el.name !== 'owner');
    },
    getAcceptedPolicies() {
      return this.getSortedFlowPolicies.filter(
        (policy) =>
          policy.status === 'accepted' ||
          !policy.status ||
          policy.identity === 'public' // for the flow created before accept or reject functionality
      );
    },
    getPendingPolicies() {
      return this.getSortedFlowPolicies.filter(
        (policy) => policy.status === 'pending' && policy.identity !== 'public'
      );
    },
    getRejectedPolicies() {
      return this.getSortedFlowPolicies.filter(
        (policy) => policy.status === 'rejected'
      );
    },
    getSortedFlowPolicies() {
      return [...this.flowPolicies].sort((a, b) => {
        if (a.access_level === 'owner') return -1;
        if (b.access_level === 'owner') return 1;
        return 0;
      });
    },
    getRunWorkflowInfo() {
      if (this.flowDetails && getIsVueBasedFormBuilder(this.flowDetails)) {
        const endpointForVersion =
          this.flowDetails.executionUrl ||
          this.flowDetails?.settings?.executionEndPoint;
        const executionAPIVersion =
          getApiEndpointVersion(endpointForVersion) || 6;
        return {
          name: 'form-builder',
          params: {
            version: `v${executionAPIVersion}`,
            processID: this.flowDetails.id || this.flowId,
          },
        };
      }

      return {
        name: 'process-runner',
        params: {
          id: this.flowDetails?.id || this.flowId,
        },
      };
    },
    runWorkflowLink() {
      if (!this.flowId) return '';
      if (!this.flows.length && !this.hasLoadedFlowDetails) return '';
      const href = this.$router.resolve(this.getRunWorkflowInfo).href;
      return href.startsWith('http')
        ? href
        : `${window.location.origin}${href}`;
    },
    hasValidRunWorkflowLink() {
      return Boolean(
        this.runWorkflowLink &&
          !this.runWorkflowLink.includes('undefined') &&
          !this.runWorkflowLink.includes('null')
      );
    },
    isActionFlow() {
      return this.flowDetails?.type === 'action';
    },
    showFlowLinkSection() {
      return !this.flows.length && !this.isActionFlow;
    },
    isModalLoading() {
      if (this.flows.length) return false;
      if (this.isActionFlow) return false;
      return this.isLoadingFlowLink || !this.hasValidRunWorkflowLink;
    },
    safeWorkflowName() {
      const sourceName = this.flowDetails?.name || `workflow-${this.flowId}`;
      return String(sourceName)
        .trim()
        .replace(/[^a-zA-Z0-9-_]/g, '_')
        .replace(/_+/g, '_');
    },
    user() {
      return this.$store.state.user;
    },
  },
  watch: {
    flowId: {
      async handler() {
        this.isFlowLinkCopied = false;
        this.flowDetails = null;
        this.hasLoadedFlowDetails = false;
        await this.loadFlowDetails();
      },
    },
  },
  components: {
    CustomModal,
    CommonButton,
    LoadingSpinner,
    CustomDropdown,
    WorkflowSharePoliciesAccordion,
    CommonQrGenerator,
  },
};
</script>

<style scoped>
.share-flow-modal ::v-deep .custom-modal-wrapper .custom-modal {
  width: 100%;
  max-width: 1060px;
}

.share-flow-modal .share-flow-modal-content-wrapper {
  width: 100%;
  height: 720px;
  max-height: calc(100vh - 80px);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 56px 56px 44px;
  overflow-y: auto;
  box-sizing: border-box;
}

@media (max-width: 767px) {
  .share-flow-modal .share-flow-modal-content-wrapper {
    padding: 28px 20px 22px;
    width: 100%;
  }
}
.share-flow-modal .content {
  width: 100%;
  height: 100%;
  padding: 0 4px;
  min-height: 100%;
}

.share-flow-modal .content.is-modal-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;
}

.share-flow-modal .share-flow-grid {
  margin-top: 26px;
  gap: 24px;
}

.share-flow-modal .flow-link-loading-wrapper {
  margin-top: 0;
  min-height: 0;
  height: auto;
  width: auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.share-flow-modal .share-flow-grid.single-column {
  display: block !important;
}

.share-flow-modal .share-flow-main-column {
  min-width: 0;
  flex: 1 1 auto;
  width: 100%;
}

.share-flow-modal .content-title {
  font-size: 34px;
  line-height: 1.1;
  color: #111111;
  margin: 0;
  font-weight: 500;
  letter-spacing: -0.02em;
  text-transform: none;
}

.share-flow-modal .content-paragraph {
  font-size: 16px;
  color: #5a5a66;
  margin: 30px 0 18px;
  max-width: 680px;
  line-height: 1.35;
}

.share-flow-modal .email-input {
  outline: none;
  border: 1px solid #c9c9ce;
  border-radius: 10px;
  width: 100%;
  font-size: 16px;
  padding: 0 18px;
  font-weight: 400;
  line-height: 54px;
  height: 58px;

  color: #111111;
  box-sizing: border-box;
}

@media (max-width: 767px) {
  .share-flow-modal .email-input {
    width: 100%;
    max-width: 100%;
  }
}
.share-flow-modal .email-input::placeholder {
  color: #9999a5;
}

.share-flow-modal .email-input.has-error {
  border-color: #d93025;
}

.share-flow-modal .form-element-wrapper {
  padding-top: 26px;
  position: relative;
}

.share-flow-modal .email-validation-message {
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(100% - 24px);
  margin: 0;
  font-size: 13px;
  line-height: 1.3;
  color: #d93025;
  padding: 0;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.share-flow-modal .custom-dropdown.permission-selector {
  border-radius: 10px;
  font-size: 14px;
  width: 300px;
  display: flex;
  height: 58px;
  box-sizing: border-box;
}
.share-flow-modal
  ::v-deep
  .custom-dropdown.permission-selector.form-input
  .custom-dropdown-toggle {
  width: 100%;
  min-height: 58px;
  line-height: 56px;
  padding: 0 18px;
  font-size: 16px;
  border: 1px solid #c9c9ce;
  border-radius: 10px;
  color: #121212;
  box-sizing: border-box;
}
.share-flow-modal
  ::v-deep
  .custom-dropdown.permission-selector.form-input
  .custom-dropdown-menu {
  padding: 0 8px;
}
.share-flow-modal
  ::v-deep
  .custom-dropdown.permission-selector.form-input
  .custom-dropdown-menu
  li {
  min-height: 46px;
}

.share-flow-modal .user-access-desc {
  font-size: 16px;
  color: #333343;
  padding: 0 6px;
}

.share-flow-modal .flow-share-contact-info {
  height: 150px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.flow-share-contact-info-wrap {
  margin-top: 22px;
  height: 187px;
  overflow: scroll;
  display: flex;
  flex-direction: column;
  gap: 26px;
}

::v-deep .dropdown-item.active {
  background: #e5e5e591;
}

.share-flow-modal .loading-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}
.share-flow-modal ::v-deep .loading-wrapper .progress-icon-wrapper {
  position: unset;
}

.share-flow-modal
  ::v-deep
  .permission-selector.form-input
  .custom-dropdown-toggle
  img {
  margin-top: 3px;
}

.share-flow-modal ::v-deep .permission-selector.active .button-icon {
  transform: rotate(180deg);
  width: 9px;
}

.share-flow-modal .input-field {
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 930px;
}

@media (max-width: 767px) {
  .share-flow-modal .input-field {
    flex-direction: column;
    align-items: flex-start;
  }
}
.share-flow-modal form {
  padding: 0;
  max-width: 930px;
}

.share-flow-modal .share-flow-modal-content-wrapper.share-multiple-flows {
  height: auto !important;
}

.share-flow-modal .flow-link-share-section {
  margin-top: 0;
}

.share-flow-modal .flow-link-main-column {
  min-width: 0;
}
.share-flow-modal .flow-link-input-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}

.share-flow-modal .flow-link-input-wrap input {
  flex: 1;
  min-width: 0;
  height: 58px;
  margin: 0;
  font-size: 15px;
  border: 1px solid #d9d9df;
  border-radius: 16px;
  padding: 0 22px;
  color: #1a1a1a;
  background: #fbfbfc;
  box-sizing: border-box;
}

.share-flow-modal .copy-flow-link-btn {
  min-width: 246px;
  height: 58px;
  margin: 0;
  padding: 0 22px;
  border: 1px solid #151515;
  border-radius: 16px;
  background: #151515;
  color: #fff;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.18s ease;
}

.share-flow-modal .copy-flow-link-btn:hover {
  background: #232323;
}

.share-flow-modal .copy-feedback {
  display: inline-flex;
  margin-top: 10px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 13px;
  line-height: 1.2;
  color: #206236;
  background: #e8f7ee;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.18s ease;
}

.share-flow-modal .copy-feedback.visible {
  opacity: 1;
  visibility: visible;
}

.share-flow-modal .flow-link-qr-wrap {
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  width: 320px;
  max-width: 320px;
  flex: 0 0 320px;
  margin-top: 10px;
}

.share-flow-modal .flow-link-qr-wrap ::v-deep .common-qr-generator {
  align-items: center;
  width: 300px;
  max-width: 100%;
}

.share-flow-modal .flow-link-qr-wrap ::v-deep .qr-code-wrapper {
  border: 1px solid #d8d8dd;
  border-radius: 14px;
  padding: 12px;
}

.share-flow-modal .flow-link-qr-wrap ::v-deep .download-qr-btn {
  width: 100%;
  height: 58px;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 600;
}

.share-flow-modal ::v-deep .common-button-component .common-button {
  min-width: 160px;
  min-height: 56px;
  border-radius: 10px;
  font-size: 14px;
  letter-spacing: 0.03em;
}
.share-flow-modal ::v-deep .common-button-component .common-button .loader {
  border-radius: 10px;
}
.share-flow-modal ::v-deep .loading .progress-icon {
  left: -30px;
}

@media (max-width: 1200px) {
  .share-flow-modal .content-title {
    font-size: 32px;
  }

  .share-flow-modal .content-paragraph {
    font-size: 16px;
  }

  .share-flow-modal .email-input {
    font-size: 16px;
    height: 52px;
    line-height: 50px;
  }

  .share-flow-modal .custom-dropdown.permission-selector {
    width: 260px;
    height: 52px;
  }

  .share-flow-modal
    ::v-deep
    .custom-dropdown.permission-selector.form-input
    .custom-dropdown-toggle {
    line-height: 50px;
    font-size: 15px;
  }

  .share-flow-modal .copy-flow-link-btn,
  .share-flow-modal .flow-link-input-wrap input,
  .share-flow-modal .flow-link-qr-wrap ::v-deep .download-qr-btn {
    height: 52px;
    font-size: 14px;
  }

  .share-flow-modal .copy-flow-link-btn {
    min-width: 200px;
  }

  .share-flow-modal ::v-deep .common-button-component .common-button {
    min-height: 48px;
    font-size: 14px;
  }
}

@media (max-width: 991px) {
  .share-flow-modal ::v-deep .custom-modal-wrapper .custom-modal {
    max-width: 900px;
  }

  .share-flow-modal .share-flow-modal-content-wrapper {
    height: 584px;
    max-height: calc(100vh - 24px);
    padding: 24px 18px 20px;
    overflow-y: auto;
  }

  .share-flow-modal .content-title {
    font-size: 30px;
  }

  .share-flow-modal .content {
    padding: 0 2px;
    min-height: 540px;
  }

  .share-flow-modal .content-paragraph {
    margin: 20px 0 14px;
  }

  .flow-share-contact-info-wrap {
    height: auto;
    max-height: 38vh;
    overflow-y: auto;
    overflow-x: hidden;
  }
}

@media (max-width: 767px) {
  .share-flow-modal ::v-deep .custom-modal-wrapper .custom-modal {
    max-width: none;
  }

  .share-flow-modal .share-flow-grid {
    gap: 40px;
  }

  .share-flow-modal .share-flow-modal-content-wrapper {
    width: 100%;
    height: 60vh;
    max-height: 60vh;
    min-height: 60vh;
    padding: 0 16px;
    margin: 50px 0;
    overflow-y: auto;
  }

  .share-flow-modal .share-flow-modal-content-wrapper.is-modal-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .share-flow-modal .share-flow-modal-content-wrapper,
  .flow-share-contact-info-wrap {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .share-flow-modal .share-flow-modal-content-wrapper::-webkit-scrollbar,
  .flow-share-contact-info-wrap::-webkit-scrollbar {
    width: 0;
    height: 0;
  }

  .share-flow-modal .content {
    padding: 0 6px;
    min-height: 0;
    height: 100%;
  }

  .share-flow-modal .content.is-modal-loading {
    width: 100%;
    min-height: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .share-flow-modal .flow-link-loading-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 100%;
    height: 100%;
  }

  .share-flow-modal .flow-link-loading-wrapper ::v-deep .loading {
    position: relative;
    height: 100%;
    width: 100%;
  }

  .share-flow-modal .flow-link-loading-wrapper ::v-deep .progress-icon-wrapper {
    top: 50%;
    left: 50%;
    right: auto;
    bottom: auto;
    margin: 0;
    transform: translate(-50%, -50%);
  }

  .share-flow-modal .flow-link-qr-wrap {
    justify-content: center;
    width: 100%;
    max-width: 100%;
    flex: 1 1 auto;
    margin-top: 0;
  }

  .share-flow-modal .flow-link-input-wrap {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .share-flow-modal .copy-flow-link-btn {
    width: 100%;
    min-width: 0;
    height: 48px;
    border-radius: 12px;
    font-size: 13px;
  }

  .share-flow-modal .flow-link-input-wrap input {
    height: 54px;
    line-height: 54px;
    min-height: 54px;
    width: 100%;
    font-size: 14px;
    border-radius: 12px;
    padding: 0 14px;
  }

  .share-flow-modal .content-title {
    font-size: 27px;
  }

  .share-flow-modal .content-paragraph {
    font-size: 14px;
    margin-top: 16px;
  }

  .share-flow-modal .custom-dropdown.permission-selector,
  .share-flow-modal .email-input {
    width: 100%;
    height: 48px;
    max-width: 100%;
  }

  .share-flow-modal .email-input {
    font-size: 15px;
    line-height: 46px;
    padding: 0 14px;
  }

  .share-flow-modal
    ::v-deep
    .custom-dropdown.permission-selector.form-input
    .custom-dropdown-toggle {
    width: 100%;
    min-height: 48px;
    line-height: 46px;
    font-size: 15px;
    padding: 0 14px;
  }

  .share-flow-modal .input-field {
    gap: 12px;
  }

  .share-flow-modal .form-element-wrapper {
    padding-top: 16px;
  }

  .share-flow-modal .flow-link-qr-wrap ::v-deep .common-qr-generator {
    width: 100%;
    max-width: 240px;
  }

  .share-flow-modal .flow-link-qr-wrap ::v-deep canvas {
    width: 160px !important;
    height: 160px !important;
  }

  .share-flow-modal .flow-link-qr-wrap ::v-deep .qr-code-wrapper {
    padding: 8px;
    border-radius: 10px;
  }

  .share-flow-modal .flow-link-qr-wrap ::v-deep .download-qr-btn {
    height: 46px;
    border-radius: 12px;
    font-size: 13px;
    margin-top: 10px;
  }

  .share-flow-modal ::v-deep .common-button-component .common-button {
    width: 100%;
    min-height: 46px;
    font-size: 13px;
  }
}

@media (min-width: 768px) and (max-height: 820px) {
  .share-flow-modal .share-flow-modal-content-wrapper {
    max-height: calc(100vh - 24px);
    overflow-y: auto;
  }
}
</style>
